import * as React from 'react';
import { useStore } from 'react-redux';
import { isEqual } from 'vet-bones/utils';
import { usePortalFeatures } from 'vet-bones/utils/portal';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { transformModelToSnapshot } from 'src/containers/ProjectDiagram/utils/transformModelToSnapshot';
import { useCreateMappings } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useCreateMappings';
import { useFetchMappingSuggestions } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useFetchMappingSuggestions';
import { pendingAutoGenerateResourceIdsSelector } from 'src/selectors/pendingAutoGenerateModel';
import { projectSelector } from 'src/selectors/project';
import { projectFileUploadsByIdSelector } from 'src/selectors/projectFileUploadsById';
import { projectVirtualGraphsByIdSelector } from 'src/selectors/projectVirtualGraphsById';
import { ReduxState } from 'src/store/types';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { useEvaluateSummary } from 'src/utils/voicebox/hooks/useEvaluateSummary';

export const useAutoGenerateModel = () => {
  const dispatch = useReduxDispatch();
  const store = useStore<ReduxState>();
  const { projectId } = useRouterParams();
  const { fetchMappingSuggestions, isLoading } = useFetchMappingSuggestions();
  const createMappings = useCreateMappings();
  const { evaluateSummary } = useEvaluateSummary();

  const portalFeatures = usePortalFeatures();
  const isVoiceboxEnabled = useIsVoiceboxEnabled();
  const project = useReduxSelector(projectSelector(projectId));
  const useVoiceboxSuggestions =
    Boolean(isVoiceboxEnabled && portalFeatures.voiceboxSuggestions) &&
    !project?.disableVoiceboxSuggestions;

  const fileUploadsById = useReduxSelector(
    projectFileUploadsByIdSelector(projectId),
    isEqual
  );

  const virtualGraphsById = useReduxSelector(
    projectVirtualGraphsByIdSelector(projectId),
    isEqual
  );

  const resourceIds = useReduxSelector(
    pendingAutoGenerateResourceIdsSelector(projectId)
  );

  const [isGenerating, setIsGenerating] = React.useState(false);
  const [hasTriggered, setHasTriggered] = React.useState(false);

  React.useEffect(() => {
    if (hasTriggered || isGenerating || isLoading) {
      return;
    }

    if (!resourceIds?.length) {
      return;
    }

    const availableResourceIds = [
      ...Object.keys(fileUploadsById),
      ...Object.keys(virtualGraphsById),
    ];
    const allResourcesAvailable = resourceIds.every((id) =>
      availableResourceIds.includes(id)
    );

    if (!allResourcesAvailable) {
      return;
    }

    setHasTriggered(true);

    const runAutoGenerate = async () => {
      setIsGenerating(true);

      try {
        const { mappingSuggestions } = await fetchMappingSuggestions({
          selectedClasses: [],
          selectedResources: resourceIds,
        });

        if (mappingSuggestions?.length) {
          const diagram = await createMappings(mappingSuggestions, true, true);
          if (useVoiceboxSuggestions && diagram) {
            const updatedProject = projectSelector(projectId)(store.getState());
            if (updatedProject?.diagram) {
              const snapshot = { ...updatedProject.diagram };
              snapshot.model = transformModelToSnapshot(diagram.model);
              evaluateSummary({ ...updatedProject, diagram: snapshot });
            }
          }
        }
      } catch (error) {
        console.error('Failed to auto-generate model:', error);
      } finally {
        dispatch(
          projectsActionCreators.clearedPendingAutoGenerateModel(projectId)
        );
        setIsGenerating(false);
        dispatch(
          uiSlice.actions.toggledCreateProjectOverlay({ isOpen: false })
        );
      }
    };

    runAutoGenerate();
  }, [
    createMappings,
    dispatch,
    evaluateSummary,
    fetchMappingSuggestions,
    fileUploadsById,
    hasTriggered,
    isGenerating,
    isLoading,
    projectId,
    resourceIds,
    store,
    useVoiceboxSuggestions,
    virtualGraphsById,
  ]);

  return { isGenerating };
};
